import type { FC } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { sortBy, isEqual } from "lodash";
import { useDispatch } from "react-redux";

import Sentry from "@similarweb/sentry-logger";
import { ScrollArea, IconButton, Button, PlainTooltip } from "@similarweb/ui-components";

import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { customSegmentCreatedUpdated } from "app/actions/segmentsModuleActions";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { usePrevious } from "app/components/hooks/usePrevious";
import { useLoading } from "app/custom-hooks/loadingHook";
import { i18nFilter } from "app/filters/i18nFilter";
import { itsMyOwnSegment } from "app/pages/segments/analysis/itsMyOwnSegment";
import { MODE } from "app/pages/segments/analysis/SegmentsAnalysisTrafficContainerMode";
import { isSegmentsLimitationExceeded, showSegmentsMeteringHook } from "app/pages/segments/metering/SegmentsMetering";
import { ClearFiltersModal } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-modals/ClearFiltersModal";
import { LongSegmentRulesErrorModal } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-modals/LongSegmentRulesErrorModal";
import { SegmentNameModal } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-modals/SegmentNameModal";
import { DomainSelection } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/DomainSelection";
import { RulePagesWrapper } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/RulePagesWrapper";
import { SegmentsValidationSection } from "app/pages/segments/redesigned-wizard/segment-wizard/segments-validation-section/SegmentsValidationSection";
import { SegmentsWebSourceSection } from "app/pages/segments/redesigned-wizard/segment-wizard/segments-websource-section/SegmentsWebSourceSection";
import { useSegmentWizardContext } from "app/pages/segments/redesigned-wizard/segment-wizard/SegmentWizardContextProvider";
import {
    isWebsiteAllowed,
    prepareSegments,
    validateSelectedSite,
    hasExistingUrlsCompoundStringNoRobotsTxt,
    getWordPredictions as getInnerWordPredictions,
    getFolderPredictions,
    getSegmentUrls,
    rulesSerializer,
    getRulesHighlightWords,
    getRulesExactMatchHighlights,
    getSegmentShare,
    onSegmentSave,
    doesAtLeastOneRuleExist,
    doesOneNonEmptyRuleExist,
    addEmptyRule,
    getResponseBodyText,
    getPercentageChangeFromTooLongSegmentRuledResponseMessage,
} from "app/pages/segments/redesigned-wizard/segment-wizard/segmentWizardHelpers";
import {
    WizardContainer,
    Wrapper,
    SegmentRulesContainer,
    SegmentRulesSectionContainer,
    ContentWrapper,
    RuleBuilderContainer,
    SegmentRulesColumnContainer,
    Title,
    SegmentRulesVerifyResultsContainer,
    SegmentRulesTitle,
    SegmentRulesStepTopContainer,
    StyledIcon,
    FiltersTitleWrapper,
    FlexedDiv,
    ClearAllButtonWrapper,
    SegmentRulesScrollAreaContainer,
    CreateButtonContainer,
    CreateButtonWrapper,
    TextFieldStyled,
} from "app/pages/segments/redesigned-wizard/segment-wizard/styledComponents";
import type {
    IRule,
    IWordPredictions,
    ISite,
    ISegmentRuleItem,
    IWordsFilterConfig,
    ISegmentUrl,
} from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { RuleTypes, ENewRuleType } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { resolveTrackingCustomAttributes } from "app/pages/segments/redesigned-wizard/segment-wizard/utils/resolveTrackingCustomAttributes";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { ICustomSegment } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import SegmentsApiService from "app/services/segments/segmentsWizardService";
import type { ITrafficData, ITrafficDataError } from "app/services/segments/segmentsWizardServiceTypes";
import { allTrackers } from "app/services/track/track";
import { AILoader } from "./ai-rules/AiLoader";
import { AiDescriptionSection } from "./ai-rules/AiRulesView";
import { AiLoaderContainer } from "./ai-rules/styledComponents";
import { BackButtonWrapper } from "./BackButtonWrapper";
import { SegmentResultsSection } from "./segment-result-section/SegmentUrlResultSection";

const segmentsApiService = new SegmentsApiService();

export const SegmentWizard: FC = () => {
    const { params } = useCurrentStateAndParams();
    const isMidTierUser = SegmentsUtils.isMidTierUser();

    const [userId, setUserId] = useState<string>(null);
    const [showSegmentNameModal, setShowSegmentNameModal] = useState<boolean>(false);
    const [showClearFiltersModal, setShowClearFiltersModal] = useState<boolean>(false);
    const [showTooLongSegmentRulesModal, setShowTooLongSegmentRulesModal] = useState<boolean>(false);
    const [showTooLongSegmentRulesErrorText, setShowTooLongSegmentRulesErrorText] = useState<boolean>(false);
    const [percentageChangeFromLimitCharacters, setPercentageChangeFromLimitCharacters] = useState<string>(null);
    const [addFiltersTitleShadow, setAddFiltersTitleShadow] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [selectedSite, setSelectedSite] = useState<ISite>(null);
    const [segmentName, setSegmentName] = useState<string>("");
    const [segmentIncludeRules, setSegmentIncludeRules] = useState<IRule[]>([]);
    const [segmentExcludeRules, setSegmentExcludeRules] = useState<IRule[]>([]);
    const [segmentShareAndVisitsData, segmentShareAndVisitsDataOps] = useLoading<ITrafficData, ITrafficDataError>();

    const isAiModeAvailable = useMemo(() => isAvailable(swSettings.components.SegmentRulesCreator), []);
    const [isAiGenerating, setIsAiGenerating] = useState(false);

    const isSelectedSiteAllowed = useMemo(() => isWebsiteAllowed(selectedSite), [selectedSite]);
    const hideUrlsPreview = useMemo(
        () => !isSelectedSiteAllowed || !selectedSite?.validation?.isRobotsTxt,
        [isSelectedSiteAllowed, selectedSite?.validation],
    );

    // if we are not on edit mode and segment limitation is exceeded the user will redirect to homepage and see a hook:
    useEffect(() => {
        const { sid } = params;
        if (!isEditMode && !sid) {
            isSegmentsLimitationExceeded().then((isExceeded) => {
                if (isExceeded) {
                    proNavigator.go("digitalsuite_segments-homepage").then(() => showSegmentsMeteringHook());
                }
            });
        }
    }, [isEditMode, params]);

    const titleLabel = useMemo(
        () =>
            isEditMode
                ? "workspaces.segment.wizard.segment.edit.title.label"
                : "workspaces.segment.wizard.segment.title.label",
        [isEditMode],
    );
    const createButtonLabel = useMemo(
        () => (isEditMode ? "segment.wizard.new.update.segment.button" : "segment.wizard.new.create.segment.button"),
        [isEditMode],
    );
    const modalConfirmButtonLabel = useMemo(
        () => (isEditMode ? "segment.wizard.new.update.segment.button" : "segment.wizard.new.create.modal.button.save"),
        [isEditMode],
    );

    const onScroll = useCallback(
        ({ topPosition }) => {
            const displayFilterTitleShadow = topPosition > 0;
            if (addFiltersTitleShadow !== displayFilterTitleShadow) {
                setAddFiltersTitleShadow(displayFilterTitleShadow);
            }
        },
        [addFiltersTitleShadow],
    );

    const onCloseSegmentRulesErrorModal = useCallback((isOpen) => {
        setShowTooLongSegmentRulesModal(isOpen);
    }, []);

    // Marks the currently active rule for fetching word predictions. we need to fetch word predictions up to the currently edited rule
    // As an example: if we're editing rule N, we want to fetch word predictions according to rules (1,2,... N).
    const [activeWordPredictionIndex, setActiveWordPredictionIndex] = useState<number>(null);

    // Marks the currently active (open) rule. that is the rule that is currently in edit mode (EditRuleMode)
    // this is used to disable other rules while editing the current active rule.
    // It is also used to know the currently edited rule, for onCloseRule function
    const [activeIndex, setActiveIndex] = useState<number>(null);

    const dispatch = useDispatch();
    const {
        segmentRules,
        selectedCountry,
        resetSegmentsWizardData,
        setSegmentsReady,
        setSegmentRules,
        requestSegmentsPreview,
        requestWordPredictions,
        receiveWordPredictions,
        receiveFolderPredictions,
        requestFolderPredictions,
        segmentsReady,
    } = useSegmentWizardContext();

    const wordHighlights = useMemo(() => getRulesHighlightWords(segmentsApiService, segmentRules), [segmentRules]);

    const exactMatchHighlights = useMemo(
        () => getRulesExactMatchHighlights(segmentsApiService, segmentRules),
        [segmentRules],
    );

    const previousSelectedCountry = usePrevious(selectedCountry);
    const previousSegmentRules = usePrevious(segmentRules);
    const previousSelectedSite = usePrevious(selectedSite);

    useEffect(() => {
        if (!isEqual(previousSegmentRules, segmentRules)) {
            const { includeRules, excludeRules } = segmentRules.reduce(
                (acc, rule) => {
                    acc[rule.type === RuleTypes.include ? "includeRules" : "excludeRules"].push(rule);
                    return acc;
                },
                { includeRules: [], excludeRules: [] },
            );
            setSegmentExcludeRules(excludeRules);
            setSegmentIncludeRules(includeRules);
        }
    }, [segmentRules, previousSegmentRules]);

    const updateSegmentShareAndVisits = useCallback(
        (hasCountryChanged) => {
            if (
                doesOneNonEmptyRuleExist(segmentRules) &&
                (!isEqual(previousSegmentRules, segmentRules) || hasCountryChanged)
            ) {
                // If selectedCountry changed and selectedCountry had a previous value
                if (segmentShareAndVisitsData.state === useLoading.STATES.LOADING) {
                    segmentShareAndVisitsDataOps.abort();
                }
                segmentShareAndVisitsDataOps.reset();
                segmentShareAndVisitsDataOps.load(() =>
                    getSegmentShare(selectedSite.name, selectedCountry.id, segmentRules, segmentsApiService),
                );
            } else if (!doesOneNonEmptyRuleExist(segmentRules)) {
                segmentShareAndVisitsDataOps.reset();
            }
        },

        [selectedSite?.name, selectedCountry?.id, segmentRules, previousSegmentRules],
    );

    useEffect(() => {
        // Country-specific share/visits stats genuinely need a selected country, but the
        // popular-pages fetch below only needs the site - selectedCountry can legitimately stay
        // unset (e.g. an account with no permitted countries), so it must not gate the whole block
        // or filters/results would be stuck disabled forever with no country selected.
        if (selectedSite?.name && segmentRules.length > 0) {
            if (
                selectedCountry?.id &&
                ((!isEqual(previousSelectedCountry, selectedCountry) && previousSelectedCountry?.id) ||
                    !isEqual(previousSegmentRules, segmentRules))
            ) {
                updateSegmentShareAndVisits(!isEqual(previousSelectedCountry, selectedCountry));
            }
            // if website allowed and this is the first update that segment rules were added (even if only the first empty rule exists), perform a fetch for segment urls according to that site
            if (
                (previousSegmentRules.length === 0 ||
                    (!doesOneNonEmptyRuleExist(previousSegmentRules) && // Handle edit segment case when previousSegments hold only placeholder empty segments
                        previousSegmentRules.length === 2)) &&
                segmentRules.length > 0 &&
                isSelectedSiteAllowed
            ) {
                prepareSegments(selectedSite, segmentsApiService, setSegmentsReady).then(); // do not await for this
            }
        } else {
            if (
                (previousSelectedSite && !selectedSite?.name) ||
                (segmentRules.length <= 0 && previousSegmentRules?.length > 2)
            ) {
                // selectedSite was deleted or all rules were deleted
                segmentShareAndVisitsDataOps.reset();
            }
            if (segmentRules.length <= 0) {
                onSegmentSave(
                    segmentRules,
                    setSegmentRules,
                    segmentIncludeRules.length,
                    false,
                    false,
                    ENewRuleType.BOTH,
                );
            }
        }
        if (segmentShareAndVisitsData.state === useLoading.STATES.ERROR) {
            const isFailedOnTooLongSegmentRules =
                !!segmentShareAndVisitsData.error.response?.headers.get("sw-segments-rules");

            if (isFailedOnTooLongSegmentRules) {
                const responseBody = segmentShareAndVisitsData.error.response.body;
                getResponseBodyText(responseBody).then((responseMessage) => {
                    const percentageChange =
                        responseMessage && getPercentageChangeFromTooLongSegmentRuledResponseMessage(responseMessage);

                    setPercentageChangeFromLimitCharacters(percentageChange);
                });
                !showTooLongSegmentRulesErrorText && setShowTooLongSegmentRulesModal(true);
                doesOneNonEmptyRuleExist(segmentRules)
                    ? setShowTooLongSegmentRulesErrorText(true)
                    : setShowTooLongSegmentRulesErrorText(false);
            }
        } else if (segmentShareAndVisitsData.state === useLoading.STATES.SUCCESS) {
            setShowTooLongSegmentRulesModal(false);
            setShowTooLongSegmentRulesErrorText(false);
        }
    }, [selectedSite?.name, selectedCountry?.id, segmentRules, isSelectedSiteAllowed, segmentShareAndVisitsData.state]);

    const segmentShareAndVisits = useMemo(
        () => ({
            segmentShare: segmentShareAndVisitsData.data?.result?.SegmentShare,
            segmentVisits: segmentShareAndVisitsData.data?.result?.SegmentVisits,
            segmentWebSource: segmentShareAndVisitsData.data?.result?.WebSource,
        }),
        [segmentShareAndVisitsData.data],
    );

    const handleSelectMainSite = async (site: ISite) => {
        allTrackers.dispatchTrackEvent("Button", "click", `segment wizard/new/select main site/${site.name || ""}`);

        const selectedSite = await validateSelectedSite(site, segmentsApiService);
        resetSegmentsWizardData();
        setSelectedSite(selectedSite);

        return selectedSite;
    };

    const handleClearMainSite = useCallback(() => {
        const { name } = selectedSite;
        allTrackers.dispatchTrackEvent("Button", "click", `segment wizard/new/clear main site/${name || ""}`);

        // Clear up the selected site, and remove any segment rules, so that
        // we won't keep any rules that are related to the previous site
        setSelectedSite(null);
        setSegmentRules([]);
        setSegmentsReady(false);
        setActiveWordPredictionIndex(null);
    }, [selectedSite]);

    const handleClearAll = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "segment wizard/new/clear all segment rules");
        setSegmentRules([]);
    }, []);

    const initializeStateFromApi = async (sid: string, createNew?: string): Promise<void> => {
        if (!createNew) {
            setIsEditMode(true);
        }
        try {
            // Load all segments data from the API
            const segmentMeta = await segmentsApiService.getCustomSegmentMetaData({ sid });

            const selectedSite = { name: segmentMeta.domain, image: segmentMeta.favicon };
            const segmentNameVal =
                itsMyOwnSegment(segmentMeta.userId) && String(createNew).toLowerCase() !== "true"
                    ? segmentMeta.segmentName
                    : `${i18nFilter()("workspaces.segment.wizard.copy.of")} ${segmentMeta.segmentName}`;
            setSelectedSite(selectedSite);
            setUserId(segmentMeta.userId);
            setSegmentName(segmentNameVal);
            await handleSelectMainSite(selectedSite);

            const existingSegmentRules = segmentMeta.rules.map((rule) => {
                const ruleWords = rule?.words ?? [];
                const ruleExact = rule?.exact ?? [];
                const ruleFolders = rule?.folders ?? [];
                const ruleExactURLS = rule?.exactURLS ?? [];

                return {
                    type: rule.type,
                    words: [...ruleWords],
                    exact: [...ruleExact],
                    folders: [...ruleFolders],
                    exactURLS: [...ruleExactURLS],
                };
            });

            addEmptyRule(RuleTypes.include, existingSegmentRules);
            const orderedSegments = sortBy(existingSegmentRules, ["type"]);

            const excludeIndex = orderedSegments.findIndex((rule) => rule.type === RuleTypes.exclude);
            let newRuleType;
            // At least one type has to exist as it's a saved segment
            if (excludeIndex > 0) {
                // Both include and exclude types exist
                // Add an empty rule for exclude, as for include it was already added:
                addEmptyRule(RuleTypes.exclude, orderedSegments);
                setSegmentRules(orderedSegments);
            } else {
                if (excludeIndex === 0) {
                    // Include type doesn't exist
                    newRuleType = RuleTypes.include;
                    // Add an empty rule for exclude, as for include it will be added in onSegmentSave:
                    addEmptyRule(RuleTypes.exclude, orderedSegments);
                } else {
                    // Exclude type doesn't exist
                    newRuleType = RuleTypes.exclude;
                }
                const segmentIncludeRulesLength = excludeIndex > 0 ? excludeIndex - 1 : 0;
                onSegmentSave(orderedSegments, setSegmentRules, segmentIncludeRulesLength, true, false, newRuleType);
            }
        } catch (e) {
            Sentry.error("Error initializing wizard state", e);
        }
    };

    const initializeState = async () => {
        const { sid, createNew } = params;
        if (sid) {
            await initializeStateFromApi(sid, createNew);
        }
    };

    useEffect(() => {
        initializeState();
        return () => {
            resetSegmentsWizardData();
            segmentsApiService.stopWorker();
        };
    }, []);

    const onClickBack = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "segment wizard/new/move back");
        setSegmentRules([]);
        history.back();
    };

    const hasExistingUrlsCompoundStringNoRobots = async (rules: IRule[]): Promise<boolean> =>
        await hasExistingUrlsCompoundStringNoRobotsTxt(rules, requestSegmentsPreview, selectedSite, segmentsApiService);

    const getWordPredictions = async (
        rules: IRule[],
        overrideWordsFilterConfig?: IWordsFilterConfig,
        ruleIndex?: number,
        site?: ISite,
    ): Promise<IWordPredictions> =>
        await getInnerWordPredictions(
            rules,
            segmentsApiService,
            overrideWordsFilterConfig,
            ruleIndex,
            site ?? selectedSite,
        );

    const updatePredictions = async (
        rules: IRule[],
        ruleIndex?: number,
        overrideWordsFilterConfig?: IWordsFilterConfig,
        selectedSite?: ISite,
    ): Promise<void> => {
        try {
            // Signal that we're about to start loading word and folder predictions
            requestWordPredictions(true);
            requestFolderPredictions(true);

            // Fetch word and folder predictions
            const [wordPredictions, folderPredictions] = await Promise.all([
                getWordPredictions(rules, overrideWordsFilterConfig, ruleIndex, selectedSite),
                getFolderPredictions(rules, segmentsApiService, ruleIndex),
            ]);

            // Store word and folder predictions on the context store
            receiveWordPredictions(wordPredictions);
            receiveFolderPredictions(folderPredictions);
        } catch (e) {
            Sentry.error(`Error while trying to get word or folder predictions. Error:`, e);
        } finally {
            requestWordPredictions(false);
            requestFolderPredictions(false);
        }
    };

    const updateWordPredictionsIfNeeded = useCallback(
        async (currentRuleIndex: number, ruleType: RuleTypes) => {
            // Adjust currentRuleIndex to correct index for exclude rules
            const adjustedCurrentRuleIndex =
                ruleType === 1 ? currentRuleIndex + segmentIncludeRules.length : currentRuleIndex;
            setActiveIndex(adjustedCurrentRuleIndex);
            if (adjustedCurrentRuleIndex !== activeWordPredictionIndex) {
                setActiveWordPredictionIndex(adjustedCurrentRuleIndex);
                await updatePredictions(segmentRules, adjustedCurrentRuleIndex);
            }
        },
        [segmentRules, activeWordPredictionIndex, segmentIncludeRules.length],
    );

    const onCloseRule = useCallback(
        (rule: IRule, ruleIndex, filterType, selectedTab) => {
            // Get last edited ruleType
            // activeIndex is adjusted to the whole segmentRules, while ruleIndex param, is not
            const currentIndex = activeIndex ? activeIndex : ruleIndex;
            const adjustedCurrentRuleIndex =
                activeIndex === null && rule.type === RuleTypes.exclude
                    ? currentIndex + segmentIncludeRules.length
                    : currentIndex;
            const newRuleTypeKey = Object.keys(ENewRuleType).find(
                (ruleType) => ENewRuleType[ruleType] === segmentRules[adjustedCurrentRuleIndex].type,
            );
            let updatedSegmentRules = [...segmentRules];
            let segmentIncludeRulesLength = segmentIncludeRules.length;
            if (!doesAtLeastOneRuleExist(rule)) {
                // After edit, the current rule ends up empty
                updatedSegmentRules = updatedSegmentRules.filter(
                    (segmentRule, index) => index !== adjustedCurrentRuleIndex,
                );
                segmentIncludeRulesLength -= 1;
            } else {
                updatedSegmentRules[adjustedCurrentRuleIndex] = rule;
            }

            onSegmentSave(
                updatedSegmentRules,
                setSegmentRules,
                segmentIncludeRulesLength,
                false,
                false,
                ENewRuleType[newRuleTypeKey],
                activeIndex,
                activeWordPredictionIndex,
                setActiveWordPredictionIndex,
            );

            // Remove the currently edited index, so that all
            // rules will be active for editing
            setActiveIndex(null);

            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `segments/wizard/new/rule/done/${filterType || ""}/${selectedTab === 1 ? "AND" : "OR"}/${rule.type === RuleTypes.include ? "include" : "exclude"}`,
            );
        },
        [activeIndex, segmentIncludeRules, segmentRules, activeWordPredictionIndex],
    );

    const getUrlsPreview = async (rules: IRule[]): Promise<ISegmentUrl[]> =>
        await getSegmentUrls(rules, segmentsApiService, requestSegmentsPreview, selectedSite);

    const onCreateOrUpdateCustomSegments = useCallback(
        async (segmentShareAndVisitsData) => {
            const { sid, createNew = false } = params;
            const serializedRules: ISegmentRuleItem[] = segmentRules.reduce(rulesSerializer, []);

            try {
                let newCustomSegment: ICustomSegment;
                const segmentParams = {
                    segmentName,
                    domain: selectedSite.name,
                    rules: serializedRules,
                };

                const { data = { result: { segmentShare: null, visits: null } } } = segmentShareAndVisitsData ?? {};

                // in case edit my segment
                if (sid && itsMyOwnSegment(userId) && !createNew) {
                    const segmentMetaData = await segmentsApiService.getCustomSegmentMetaData({
                        sid,
                    });
                    newCustomSegment = await segmentsApiService.updateCustomSegment({
                        ...segmentParams,
                        id: sid,
                        targetType: segmentMetaData.targetType,
                    });
                    const customAttributes = resolveTrackingCustomAttributes(newCustomSegment, data.result);
                    allTrackers.dispatchTrackEvent("Button", "click", "segmentWizard/new/updateCustomSegment", {
                        ...customAttributes,
                        country: selectedCountry.id,
                    });
                }
                //in cases 1.create new segment 2.copy and edit organization segment 3.copy and edit my segment
                else {
                    newCustomSegment = await segmentsApiService.createCustomSegment(segmentParams);

                    const customAttributes = resolveTrackingCustomAttributes(newCustomSegment, data.result);
                    const eventNameSuffix = sid ? "copyCustomSegment" : "createCustomSegment";
                    const eventName = `segmentWizard/new/${eventNameSuffix}`;
                    allTrackers.dispatchTrackEvent("Button", "click", eventName, {
                        ...customAttributes,
                        country: selectedCountry.id,
                    });
                }
                dispatch(customSegmentCreatedUpdated(newCustomSegment));
                await PreferencesService.add({
                    [SegmentsUtils.getSegmentsAnalysisPrefKey(newCustomSegment.id)]: {
                        country: selectedCountry.id.toString(),
                    },
                });
                const currentModule = proNavigator.getCurrentModule();
                const segmentAnalysisStateName = `${currentModule}-analysis-traffic`;
                proNavigator.go(segmentAnalysisStateName, {
                    ...SegmentsUtils.getPageFilterParams(newCustomSegment.id),
                    mode: MODE.single,
                    id: newCustomSegment.id,
                    country: selectedCountry.id,
                    ignoreFilterConsistency: true,
                });
            } catch (e) {
                Sentry.error("Failed serializing rules in SegmentWizard", e);
            }
        },
        [segmentRules, selectedSite, selectedCountry, userId, segmentName],
    );

    const resetActiveIndex = useCallback(() => setActiveIndex(null), []);

    const onCreateButtonClicked = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "Create segment button that open the pop up");
        setShowSegmentNameModal(true);
    }, []);

    const onCreateModalClose = useCallback(() => {
        setShowSegmentNameModal(false);
    }, []);

    const onCreateSegmentConfirm = useCallback(
        (segmentShareAndVisitsData) => {
            allTrackers.dispatchTrackEvent(
                "Button",
                "edit",
                `segment wizard/new/change segment name/${segmentName || ""}/${selectedSite?.name || ""}`,
            );
            onCreateOrUpdateCustomSegments(segmentShareAndVisitsData).finally(() => onCreateModalClose());
        },
        [onCreateOrUpdateCustomSegments, segmentName, selectedSite],
    );

    const onClearFiltersButtonClicked = useCallback(() => {
        setShowClearFiltersModal(true);
    }, []);

    const onClearFiltersModalClose = useCallback(() => {
        setShowClearFiltersModal(false);
    }, []);

    const onClearFiltersConfirm = useCallback(() => {
        handleClearAll();
        onClearFiltersModalClose();
    }, [onClearFiltersModalClose]);

    const isCreateButtonDisabled = useMemo(
        () => segmentRules.length < 3 || !segmentsReady || showTooLongSegmentRulesErrorText,
        [segmentRules.length, segmentsReady, showTooLongSegmentRulesErrorText],
    );

    const isMidtierUserAndOneRuleCreated = useMemo(
        () => isMidTierUser && segmentRules.length > 2,
        [isMidTierUser, segmentRules],
    );

    const namePlaceholder = useMemo(
        () =>
            i18nFilter()("segment.wizard.new.create.modal.text.placeholder", {
                site: selectedSite?.name,
            }),
        [selectedSite?.name],
    );

    const onTextFieldChange = useCallback((text) => {
        setSegmentName(text);
    }, []);

    const isClearAllButtonEnabled = useMemo(() => segmentRules.length > 2, [segmentRules.length]);

    const segmentNameRef = useRef(segmentName);
    segmentNameRef.current = segmentName;

    const handleRulesGenerated = useCallback(
        (rules: IRule[], description: string) => {
            onSegmentSave(
                rules,
                setSegmentRules,
                rules.filter((r) => r.type === RuleTypes.include).length,
                true,
                false,
                ENewRuleType.BOTH,
            );
            if (selectedSite && isSelectedSiteAllowed) {
                prepareSegments(selectedSite, segmentsApiService, setSegmentsReady);
            }
            if (!segmentNameRef.current && description) {
                setSegmentName(description);
            }
        },
        [selectedSite, isSelectedSiteAllowed, setSegmentRules, setSegmentsReady],
    );

    const selectedCountryId = useMemo(
        () => (selectedCountry?.id ? String(selectedCountry.id) : "us"),
        [selectedCountry?.id],
    );

    return (
        <WizardContainer>
            <Wrapper>
                <ContentWrapper>
                    <SegmentRulesColumnContainer>
                        <RuleBuilderContainer>
                            <BackButtonWrapper
                                onClickBack={onClickBack}
                                backButtonText={i18nFilter()("workspaces.marketing.wizard.goback")}
                            />
                            <Title>{i18nFilter()(titleLabel)}</Title>
                            <SegmentRulesContainer>
                                <SegmentRulesTitle>{i18nFilter()("segmentsWizard.addWebsite.title")}</SegmentRulesTitle>
                                <DomainSelection
                                    selectedSite={selectedSite}
                                    onSelectMainSite={handleSelectMainSite}
                                    onClearMainSite={handleClearMainSite}
                                    isSelectedSiteAllowed={isSelectedSiteAllowed}
                                />
                                {isAiModeAvailable && (
                                    <AiDescriptionSection
                                        domain={selectedSite?.name}
                                        country={selectedCountryId}
                                        defaultOpen={!params.sid}
                                        onRulesGenerated={handleRulesGenerated}
                                        onGeneratingChange={setIsAiGenerating}
                                    />
                                )}
                                {isAiGenerating ? (
                                    <AiLoaderContainer>
                                        <AILoader
                                            title={
                                                i18nFilter()("segment.wizard.ai.generating.label") ||
                                                "Generating segment rules..."
                                            }
                                        />
                                    </AiLoaderContainer>
                                ) : (
                                    <SegmentRulesStepTopContainer>
                                        <FiltersTitleWrapper
                                            disabled={!segmentsReady}
                                            addFiltersTitleShadow={addFiltersTitleShadow}
                                        >
                                            <FlexedDiv>
                                                <SegmentRulesTitle>
                                                    {i18nFilter()("segmentsWizard.addFilters.title")}
                                                </SegmentRulesTitle>
                                                <span>
                                                    <PlainTooltip
                                                        tooltipContent={i18nFilter()(
                                                            "segmentsWizard.addFilters.info.icon.tooltip",
                                                        )}
                                                    >
                                                        <span>
                                                            <StyledIcon
                                                                iconName="info"
                                                                size="xs"
                                                            />
                                                        </span>
                                                    </PlainTooltip>
                                                </span>
                                            </FlexedDiv>
                                            <ClearAllButtonWrapper disabled={!isClearAllButtonEnabled}>
                                                <Button
                                                    type="flat"
                                                    isDisabled={!isClearAllButtonEnabled}
                                                    onClick={onClearFiltersButtonClicked}
                                                >
                                                    {i18nFilter()("forms.buttons.clearall")}
                                                </Button>
                                            </ClearAllButtonWrapper>
                                        </FiltersTitleWrapper>
                                        <SegmentRulesSectionContainer disabled={!segmentsReady}>
                                            <SegmentRulesScrollAreaContainer>
                                                <ScrollArea
                                                    verticalScrollbarStyle={{
                                                        borderRadius: 8,
                                                        width: 5,
                                                    }}
                                                    verticalContainerStyle={{ width: 5 }}
                                                    onScroll={onScroll}
                                                >
                                                    <RulePagesWrapper
                                                        segmentIncludeRules={segmentIncludeRules}
                                                        segmentExcludeRules={segmentExcludeRules}
                                                        isMidTierUser={isMidTierUser}
                                                        domain={selectedSite?.name}
                                                        getUrlsPreview={getUrlsPreview}
                                                        hideUrlsPreview={hideUrlsPreview}
                                                        hasExistingUrlsCompoundStringNoRobots={
                                                            hasExistingUrlsCompoundStringNoRobots
                                                        }
                                                        updateWordPredictionsIfNeeded={updateWordPredictionsIfNeeded}
                                                        onCloseRule={onCloseRule}
                                                        isMidtierUserAndOneRuleCreated={isMidtierUserAndOneRuleCreated}
                                                        resetActiveIndex={resetActiveIndex}
                                                    />
                                                </ScrollArea>
                                            </SegmentRulesScrollAreaContainer>
                                        </SegmentRulesSectionContainer>
                                    </SegmentRulesStepTopContainer>
                                )}
                            </SegmentRulesContainer>
                        </RuleBuilderContainer>
                    </SegmentRulesColumnContainer>
                    <SegmentRulesVerifyResultsContainer>
                        <SegmentsValidationSection
                            segmentShare={segmentShareAndVisits?.segmentShare}
                            segmentVisits={segmentShareAndVisits?.segmentVisits}
                        />
                        <SegmentsWebSourceSection webSource={segmentShareAndVisits?.segmentWebSource} />
                        <SegmentResultsSection
                            selectedSite={selectedSite}
                            getUrlsPreview={getUrlsPreview}
                            rulesHighlightWords={wordHighlights}
                            rulesExactMatchHighlights={exactMatchHighlights}
                            hideUrlsPreview={hideUrlsPreview}
                            isSelectedSiteAllowed={isSelectedSiteAllowed}
                            showTooLongSegmentRulesErrorText={showTooLongSegmentRulesErrorText}
                            percentageChangeFromLimitCharacters={percentageChangeFromLimitCharacters}
                        />
                    </SegmentRulesVerifyResultsContainer>
                    <CreateButtonContainer>
                        <CreateButtonWrapper isEnabled={!isCreateButtonDisabled}>
                            <IconButton
                                isDisabled={isCreateButtonDisabled}
                                width={"90%"}
                                iconSize={"sm"}
                                type="primary"
                                iconName={"wand"}
                                label={i18nFilter()(createButtonLabel)}
                                onClick={onCreateButtonClicked}
                            />
                        </CreateButtonWrapper>
                    </CreateButtonContainer>
                </ContentWrapper>
            </Wrapper>
            <LongSegmentRulesErrorModal
                isOpen={showTooLongSegmentRulesModal}
                onCloseModal={onCloseSegmentRulesErrorModal}
                percentageChangeFromLimitCharacters={percentageChangeFromLimitCharacters}
            />
            <SegmentNameModal
                onModalClose={onCreateModalClose}
                isOpen={showSegmentNameModal}
                isDisabledConfirmButton={segmentName.length < 3}
                onConfirm={() => onCreateSegmentConfirm(segmentShareAndVisitsData)}
                modalConfirmButtonLabel={modalConfirmButtonLabel}
            >
                <TextFieldStyled
                    placeholder={namePlaceholder}
                    defaultValue={segmentName}
                    onChange={onTextFieldChange}
                />
            </SegmentNameModal>
            <ClearFiltersModal
                onModalClose={onClearFiltersModalClose}
                isOpen={showClearFiltersModal}
                onConfirm={onClearFiltersConfirm}
            />
        </WizardContainer>
    );
};
